import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { usePublicPageSection } from "../../../../hooks/usePublicPageSection";
import { getCaseById } from "../../../../api/casesApi";
import { resolveMediaUrl } from "../../../../utils/mediaUrl";
import { useLanguage } from "../../../../context/LanguageContext";
import "./Hero.css";

const requireField = (fields, key) => {
  const value = fields?.[key];
  if (value == null || String(value).trim() === "") {
    throw new Error(`Track hero field "${key}" is missing from the API response.`);
  }
  return value;
};

const requireMediaUrl = (section, groupKey) => {
  const item = section.media?.groups?.[groupKey]?.[0] || null;
  if (!item?.url) {
    throw new Error(
      `Track hero media group "${groupKey}" is missing from the API response.`
    );
  }
  return resolveMediaUrl(item.url);
};

const buildModel = (section) => {
  const f = section.fields || {};
  return {
    title: requireField(f, "title"),
    subtitle: requireField(f, "subtitle"),
    caseIdLabel: requireField(f, "caseIdLabel"),
    caseIdDefault: requireField(f, "caseIdDefault"),
    caseIdPlaceholder: requireField(f, "caseIdPlaceholder"),
    submitButton: requireField(f, "submitButton"),
    helpText: requireField(f, "helpText"),
    helpLinkText: requireField(f, "helpLinkText"),
    helpLinkUrl: requireField(f, "helpLinkUrl"),
    errorText: requireField(f, "errorText"),
    fileSearchIcon: requireMediaUrl(section, "file-search"),
    searchIcon: requireMediaUrl(section, "search"),
    searchXIcon: requireMediaUrl(section, "search-x"),
  };
};

const Hero = ({ onFound }) => {
  const { language, meta } = useLanguage();
  const { model, error, isLoading } = usePublicPageSection("track", "hero", buildModel);
  const [caseId, setCaseId] = useState("");
  const [showError, setShowError] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (model) {
      setCaseId("");
      setShowError(false);
    }
  }, [language, model]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = caseId.trim();

    if (!trimmed) {
      setShowError(true);
      return;
    }

    setChecking(true);
    setShowError(false);
    try {
      await getCaseById(trimmed, language);
      onFound?.(trimmed);
    } catch {
      setShowError(true);
    } finally {
      setChecking(false);
    }
  };

  if (isLoading) {
    return (
      <section className="track-hero" dir={meta.dir}>
        <div className="track-hero__content">
          <p style={{ color: "#64748b", fontSize: 14 }}>Loading…</p>
        </div>
      </section>
    );
  }

  if (error || !model) {
    return (
      <section className="track-hero" dir={meta.dir}>
        <div className="track-hero__content">
          <p style={{ color: "#b91c1c", fontSize: 14 }}>
            {error || "Track hero unavailable."}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="track-hero" dir={meta.dir}>
      <div className="track-hero__content">
        <div className="track-hero__icon" aria-hidden="true">
          <img src={model.fileSearchIcon} alt="" />
        </div>

        <h1 className="track-hero__title">{model.title}</h1>
        <p className="track-hero__subtitle">{model.subtitle}</p>

        <form className="track-hero__form" onSubmit={handleSubmit}>
          <label className="track-hero__label" htmlFor="case-id">
            {model.caseIdLabel}
          </label>

          <div className="track-hero__row">
            <input
              id="case-id"
              type="text"
              className="track-hero__input"
              value={caseId}
              onChange={(event) => {
                setCaseId(event.target.value);
                if (showError) setShowError(false);
              }}
              placeholder={model.caseIdPlaceholder}
              autoComplete="off"
              spellCheck={false}
            />
            <button type="submit" className="track-hero__submit" disabled={checking}>
              <img src={model.searchIcon} alt="" aria-hidden="true" />
              <span>{checking ? "…" : model.submitButton}</span>
            </button>
          </div>

          <p className="track-hero__help">
            {model.helpText}{" "}
            <Link to={model.helpLinkUrl}>{model.helpLinkText}</Link>
          </p>
        </form>

        {showError && (
          <div className="track-hero__error" role="alert">
            <img src={model.searchXIcon} alt="" aria-hidden="true" />
            <p>{model.errorText}</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Hero;
